/* Nomsa's noise · animated recreation */
import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";

const NAVY = "#0A1F44";
const GOLD = "#C9A24A";

const CARDS = [
  // { x%, y%, w, delay, kind, ...data }
  { x: 7, y: 12, w: 210, delay: 0.05, kind: "chat", side: "WhatsApp", text: "Are you ready to grow your wealth? Speak to a consultant today.", time: "10:24" },
  { x: 38, y: 6, w: 240, delay: 0.25, kind: "video", label: "AI GENERATED" },
  { x: 65, y: 12, w: 240, delay: 0.4, kind: "news", head: "AI is transforming investing. Are you ready?" },
  { x: 88, y: 6, w: 180, delay: 0.6, kind: "menu", items: ["Bank", "Accounts", "Investments", "Insurance", "Retirement"] },
  { x: 7, y: 40, w: 220, delay: 0.8, kind: "adviser", name: "Trusted Financial Adviser" },
  { x: 30, y: 25, w: 200, delay: 1.0, kind: "ticker" },
  { x: 88, y: 30, w: 195, delay: 1.15, kind: "alert", head: "Don't miss out!", body: "Limited time offer. Guaranteed returns.", time: "11:05" },
  { x: 70, y: 42, w: 220, delay: 1.35, kind: "pdf", head: "Investment Proposal" },
  { x: 6, y: 68, w: 220, delay: 1.5, kind: "realestate", head: "Exclusive Investment Opportunity", body: "High returns. A brighter tomorrow.", cta: "Learn more" },
  { x: 33, y: 78, w: 190, delay: 1.65, kind: "email", body: "Limited time offer! Guaranteed returns.", time: "09:17" },
  { x: 65, y: 78, w: 220, delay: 1.8, kind: "chart" },
  { x: 88, y: 62, w: 200, delay: 1.95, kind: "wealth", head: "WEALTH WITHOUT LIMITS", sub: "A smarter tomorrow" },
  { x: 5, y: 88, w: 210, delay: 2.1, kind: "instagram", body: "Build the life you want.", likes: "1.3K", comments: "412" },
  { x: 88, y: 88, w: 190, delay: 2.25, kind: "contact" },
];

export default function NomsaNoiseDemo() {
  const [params] = useSearchParams();
  const embed = params.get("embed") === "1";

  const scene = (
    <div className="relative w-full h-full overflow-hidden" data-testid="nomsa-noise"
      style={{ background: "radial-gradient(ellipse at 50% 55%, #1E3A8A20 0%, #0A0F1F 70%, #050914 100%)" }}>
      {/* faint blurred face grid */}
      <FaceGrid />
      {/* radial warm glow behind Nomsa */}
      <div className="absolute" style={{
        left: "50%", top: "60%", transform: "translate(-50%, -50%)",
        width: 640, height: 640, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(255,180,90,0.28) 0%, rgba(255,120,50,0.10) 40%, transparent 70%)",
        filter: "blur(30px)", pointerEvents: "none",
      }} />
      {/* Nomsa silhouette */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1, delay: 0.1 }}
        className="absolute" style={{ left: "50%", bottom: 0, transform: "translateX(-50%)", width: 420, height: 560, pointerEvents: "none" }}
      >
        <NomsaSilhouette />
      </motion.div>
      {/* Floating cards */}
      {CARDS.map((c, i) => (
        <FloatingCard key={i} card={c} index={i} />
      ))}
      {/* subtle bottom bar */}
      <div className="absolute bottom-0 left-0 right-0 h-24 pointer-events-none"
        style={{ background: "linear-gradient(to top, rgba(5,9,20,0.9), transparent)" }} />
    </div>
  );

  if (embed) {
    return (
      <div className="w-screen h-screen" data-testid="nomsa-noise-embed"
        style={{ background: "#050914" }}>{scene}</div>
    );
  }
  return (
    <div className="w-full" style={{ background: "#050914", minHeight: "100vh" }}>
      <div className="mx-auto" style={{ maxWidth: 1600, aspectRatio: "16/9" }}>{scene}</div>
    </div>
  );
}

function FloatingCard({ card, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.6, y: 20 }}
      animate={{
        opacity: [0, 1, 1],
        scale: [0.6, 1.08, 1],
        y: [20, -4, 4, -4, 4],
      }}
      transition={{
        opacity: { duration: 0.5, delay: card.delay, times: [0, 0.4, 1] },
        scale:   { duration: 0.7, delay: card.delay, times: [0, 0.5, 1], ease: "easeOut" },
        y: {
          duration: 4 + (index % 3),
          delay: card.delay + 0.7,
          times: [0, 0.25, 0.5, 0.75, 1],
          repeat: Infinity,
          ease: "easeInOut",
        },
      }}
      className="absolute"
      style={{
        left: `${card.x}%`, top: `${card.y}%`,
        width: card.w,
        transform: `translate(-50%, -50%) rotateY(${index % 2 ? 3 : -3}deg)`,
        transformStyle: "preserve-3d",
      }}
    >
      <CardBody card={card} />
    </motion.div>
  );
}

function CardBody({ card }) {
  const base = "rounded-2xl backdrop-blur-md shadow-2xl overflow-hidden border";
  const borderStyle = { borderColor: "rgba(120, 200, 255, 0.35)", background: "rgba(15, 30, 55, 0.55)", boxShadow: "0 0 24px rgba(90, 180, 255, 0.15), inset 0 1px 0 rgba(255,255,255,0.08)" };
  switch (card.kind) {
    case "chat": return (
      <div className={base} style={borderStyle}>
        <div className="p-3 flex gap-2 bg-white rounded-2xl">
          <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "#25D366" }}>
            <span className="text-white text-[13px]">✆</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] text-slate-800 leading-snug">{card.text}</div>
            <div className="text-[9px] text-slate-500 mt-0.5 text-right">{card.time} ✓✓</div>
          </div>
        </div>
      </div>
    );
    case "video": return (
      <div className={base} style={borderStyle}>
        <div className="relative bg-black rounded-2xl aspect-video flex items-center justify-center">
          <div className="w-10 h-10 rounded-full bg-white/25 flex items-center justify-center">▶</div>
          <div className="absolute top-2 left-2 flex items-center gap-1 bg-black/40 rounded px-2 py-0.5">
            <span className="w-2 h-2 rounded-full bg-red-500"/>
            <span className="text-[9px] text-white uppercase tracking-widest">{card.label}</span>
          </div>
        </div>
      </div>
    );
    case "news": return (
      <div className={base} style={borderStyle}>
        <div className="p-3 bg-white rounded-2xl">
          <div className="inline-block px-1.5 py-0.5 bg-red-600 text-white text-[9px] font-bold tracking-widest mb-1">NEWS</div>
          <div className="text-[12px] font-semibold leading-tight text-slate-900">{card.head}</div>
        </div>
      </div>
    );
    case "menu": return (
      <div className={base} style={borderStyle}>
        <div className="p-3 bg-white/95 rounded-2xl space-y-1.5">
          {card.items.map((it, i) => (
            <div key={i} className="flex items-center gap-2 text-[11px] font-medium" style={{ color: NAVY }}>
              <div className="w-5 h-5 rounded flex items-center justify-center text-[9px] text-white"
                style={{ background: ["#3B82F6","#8B5CF6","#EC4899","#F59E0B","#10B981"][i] }}>■</div>
              {it}
            </div>
          ))}
        </div>
      </div>
    );
    case "adviser": return (
      <div className={base} style={borderStyle}>
        <div className="relative bg-slate-900 rounded-2xl aspect-video flex items-end p-2">
          <div className="absolute top-2 left-2 w-4 h-4 rounded-full bg-red-500"/>
          <div className="text-[10px] text-white font-semibold">{card.name}</div>
        </div>
      </div>
    );
    case "ticker": return (
      <div className={base} style={borderStyle}>
        <div className="p-3 bg-slate-900 rounded-2xl">
          {[["JSE","+1.2%","g"],["S&P 500","+0.8%","g"],["NASDAQ","-0.3%","r"],["GOLD","+0.6%","g"]].map(([k,v,c],i)=>(
            <div key={i} className="flex justify-between text-[10.5px] mb-0.5">
              <span className="text-slate-300">{k}</span>
              <span className={c==="g"?"text-green-400":"text-red-400"}>{v} {c==="g"?"▲":"▼"}</span>
            </div>
          ))}
          <svg viewBox="0 0 100 20" className="w-full h-6 mt-1">
            <path d="M0 15 L20 12 L40 14 L60 8 L80 10 L100 5" stroke="#10B981" fill="none" strokeWidth="1"/>
          </svg>
        </div>
      </div>
    );
    case "alert": return (
      <div className={base} style={borderStyle}>
        <div className="p-3 bg-white rounded-2xl flex gap-2">
          <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center text-red-600 text-[13px] flex-shrink-0">⚠</div>
          <div className="flex-1">
            <div className="text-[11px] font-semibold text-slate-900">{card.head}</div>
            <div className="text-[10px] text-slate-600 leading-tight">{card.body}</div>
            <div className="text-[8px] text-slate-400 text-right mt-0.5">{card.time}</div>
          </div>
        </div>
      </div>
    );
    case "pdf": return (
      <div className={base} style={borderStyle}>
        <div className="p-3 bg-white rounded-2xl flex items-center gap-2">
          <div className="w-9 h-11 rounded flex items-center justify-center text-white text-[9px] font-bold" style={{ background: "#DC2626" }}>PDF</div>
          <div className="flex-1">
            <div className="text-[12px] font-semibold text-slate-900">{card.head}</div>
            <div className="h-1 mt-1.5 rounded bg-slate-200"/>
            <div className="h-1 mt-1 rounded bg-slate-200 w-3/4"/>
            <div className="h-1 mt-1 rounded bg-slate-200 w-2/3"/>
          </div>
        </div>
      </div>
    );
    case "realestate": return (
      <div className={base} style={borderStyle}>
        <div className="rounded-2xl bg-white overflow-hidden">
          <div className="h-20 bg-gradient-to-br from-orange-400 via-pink-400 to-purple-500"/>
          <div className="p-2">
            <div className="text-[11px] font-semibold text-slate-900">{card.head}</div>
            <div className="text-[9px] text-slate-600 mt-0.5">{card.body}</div>
            <div className="mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-semibold text-white"
              style={{ background: "#3B82F6" }}>{card.cta} →</div>
          </div>
        </div>
      </div>
    );
    case "email": return (
      <div className={base} style={borderStyle}>
        <div className="p-3 bg-white rounded-2xl flex gap-2">
          <div className="w-8 h-8 rounded flex items-center justify-center text-white flex-shrink-0" style={{ background: "#3B82F6" }}>✉</div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-semibold text-slate-900 leading-tight">Email</div>
            <div className="text-[10px] text-slate-600 leading-tight">{card.body}</div>
            <div className="text-[9px] text-slate-400 mt-1 flex justify-between"><span>Inbox</span><span>{card.time}</span></div>
          </div>
        </div>
      </div>
    );
    case "chart": return (
      <div className={base} style={borderStyle}>
        <div className="p-3 bg-slate-900 rounded-2xl">
          <div className="flex items-baseline justify-between mb-1">
            <div className="text-[16px] font-bold text-green-400">+12.4%</div>
          </div>
          <svg viewBox="0 0 100 30" className="w-full h-10">
            <path d="M0 25 L15 22 L30 18 L45 15 L60 10 L80 6 L100 3" stroke="#10B981" fill="none" strokeWidth="1.5"/>
          </svg>
          <ul className="text-[9px] text-white space-y-0.5 mt-1">
            <li>• Invest</li><li>• Grow</li><li>• Secure your future</li>
          </ul>
        </div>
      </div>
    );
    case "wealth": return (
      <div className={base} style={borderStyle}>
        <div className="rounded-2xl overflow-hidden">
          <div className="h-16 bg-gradient-to-br from-indigo-800 via-purple-700 to-pink-600"/>
          <div className="p-2 bg-slate-900">
            <div className="text-[11px] font-bold text-white leading-tight">{card.head}</div>
            <div className="text-[9px] text-slate-400 mt-0.5">{card.sub}</div>
          </div>
        </div>
      </div>
    );
    case "instagram": return (
      <div className={base} style={borderStyle}>
        <div className="rounded-2xl overflow-hidden">
          <div className="h-20 bg-gradient-to-br from-amber-500 via-red-500 to-fuchsia-600 relative">
            <div className="absolute top-1.5 left-1.5 w-5 h-5 rounded-md bg-gradient-to-tr from-yellow-400 to-pink-500"/>
          </div>
          <div className="p-2 bg-slate-900">
            <div className="text-[11px] text-white font-semibold flex items-center gap-1">
              {card.body} <span className="text-blue-400">✓</span>
            </div>
            <div className="text-[9px] text-slate-400 mt-0.5 flex gap-3">
              <span>♥ {card.likes}</span><span>💬 {card.comments}</span>
            </div>
          </div>
        </div>
      </div>
    );
    case "contact": return (
      <div className={base} style={borderStyle}>
        <div className="p-2.5 bg-white rounded-2xl flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-slate-500 text-[13px]">◔</div>
          <div className="flex-1 space-y-1">
            <div className="h-1.5 rounded bg-slate-300 w-full"/>
            <div className="h-1.5 rounded bg-slate-200 w-2/3"/>
          </div>
        </div>
      </div>
    );
    default: return null;
  }
}

function FaceGrid() {
  const rows = 3, cols = 8;
  const tiles = [];
  for (let r = 0; r < rows; r++)
    for (let c = 0; c < cols; c++) tiles.push({ r, c });
  return (
    <div className="absolute inset-0 opacity-25" style={{ filter: "blur(2px)" }}>
      {tiles.map(({ r, c }, i) => (
        <div key={i} className="absolute rounded-md"
          style={{
            left:  `${(c / cols) * 100}%`,
            top:   `${(r / rows) * 100}%`,
            width:  `${100 / cols}%`,
            height: `${100 / rows}%`,
            background: `linear-gradient(${45 + (i * 37) % 90}deg, hsl(${(i*30)%360},20%,25%) 0%, hsl(${(i*47)%360},20%,15%) 100%)`,
            border: "1px solid rgba(80,140,220,0.15)",
          }}
        />
      ))}
    </div>
  );
}

function NomsaSilhouette() {
  return (
    <svg viewBox="0 0 200 260" preserveAspectRatio="xMidYEnd meet" className="w-full h-full">
      <defs>
        <linearGradient id="silhg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#0B1220"/>
          <stop offset="1" stopColor="#03060D"/>
        </linearGradient>
        <radialGradient id="rim" cx="0.5" cy="0.2" r="0.6">
          <stop offset="0" stopColor="rgba(255,180,90,0.35)"/>
          <stop offset="1" stopColor="rgba(255,180,90,0)"/>
        </radialGradient>
      </defs>
      {/* Rim light behind */}
      <ellipse cx="100" cy="70" rx="65" ry="55" fill="url(#rim)"/>
      {/* Hair bun */}
      <ellipse cx="100" cy="55" rx="26" ry="18" fill="url(#silhg)"/>
      {/* Head */}
      <ellipse cx="100" cy="95" rx="34" ry="42" fill="url(#silhg)"/>
      {/* Ear + earring hint */}
      <circle cx="132" cy="105" r="3" fill="rgba(255,200,120,0.4)"/>
      {/* Neck + shoulders */}
      <path d="M75 130 L60 155 L30 200 L30 260 L170 260 L170 200 L140 155 L125 130 Z" fill="url(#silhg)"/>
    </svg>
  );
}
